import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";



export default function UpgradeStaggeredPackagePage({ packageData }) {
    const { wheel, tireOptions = [] } = packageData;
    // console.log("wheel", wheel);


    const [loader, setLoader] = useState(false);
    const [selectedTire, setSelectedTire] = useState(tireOptions?.[0] || null);
    const [activeThumbIndex, setActiveThumbIndex] = useState(0);

    const navigate = useNavigate();
    const mainUrl = process.env.REACT_APP_EXPRESS_APP_URL;
    const PACKAGE_QTY = 2;

    const wheelTotal = useMemo(() => {
        return (
            Number(wheel?.front_wheel_price || 0) * 2 +
            Number(wheel?.rear_wheel_price || 0) * 2
        );
    }, [wheel]);

    const packageTotal = useMemo(() => {
        if (!selectedTire) return "0.00";
        const tireTotal =
            Number(selectedTire.front_tire_price || 0) * 2 +
            Number(selectedTire.rear_tire_price || 0) * 2;
        return (wheelTotal + tireTotal).toFixed(2);
    }, [selectedTire, wheelTotal]);

    const wheelImages = [
        wheel?.image_url1, wheel?.image_url2, wheel?.image_url3, wheel?.image_url4
    ].filter(Boolean);

    const [selectedImage, setSelectedImage] = useState(wheel?.image_url1 || wheelImages[0] || "");


    // ==================== ROBUST UNIQUE KEY ====================
    const getTireSetKey = (tire) => {
        if (!tire) return "no-tire";
        const front = tire.front_tire_id || tire.front_tire_sku || "unknown";
        const rear = tire.rear_tire_id || tire.rear_tire_sku || "unknown";
        return `${front}-${rear}`;
    };

    const getSafeImage = (image) => {
        return image && image.trim() !== "" ? image : "";
    };

    // ==================== ADD TO CART LOGIC ====================
    const addItem = async (params) => {
        const cartItems = JSON.parse(localStorage.getItem("totalCartItems")) || {};

        const config = {
            method: "post",
            url: `${mainUrl}/api/add-to-cart`,
            withCredentials: true,
            params: {
                product_image: getSafeImage(params.image),
                product_id: params.product_id,
                product_name: params.product_name,
                product_price: params.product_price,
                product_quantity: params.quantity,
                category_type: params.category_type,
                total_quantity: params.total_quantity,
                part_number: params.product_id,
                vendor_id: params.vendor_id || "",
            },
            data: {
                cart_key: localStorage.getItem("cart-key") || "",
                user_token: localStorage.getItem("user-token") || "",
            },
        };

        const response = await axios.request(config);

        cartItems[params.product_id] = (cartItems[params.product_id] || 0) + params.quantity;
        localStorage.setItem("totalCartItems", JSON.stringify(cartItems));

        if (response.data.addCartResponse) {
            localStorage.setItem("cart-key", response.data.addCartResponse);
        }
    };

    const checkStock = (cartItems, product_id, addingQty, availableStock = 99, itemName) => {
        const currentQty = cartItems[product_id] || 0;
        if (currentQty + addingQty > availableStock) {
            toast.info(`Cannot add more ${itemName}. Only ${availableStock} available!`,
                {
                    position: "top-center",
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored",
                }
            );
            return false;
        }
        return true;
    };

    const addPackageToCart = async () => {
        if (!selectedTire) {
            toast.warning("Please select a tire option");
            return;
        }
        setLoader(true);

        try {
            const cartItems = JSON.parse(localStorage.getItem("totalCartItems")) || {};

            const canAdd =
                checkStock(cartItems, wheel.front_wheel_sku, PACKAGE_QTY, wheel.front_wheel_quantity, "Front Wheel") &&
                checkStock(cartItems, wheel.rear_wheel_sku, PACKAGE_QTY, wheel.rear_wheel_quantity, "Rear Wheel") &&
                checkStock(cartItems, selectedTire.front_tire_sku, PACKAGE_QTY, selectedTire.front_tire_quantity, "Front Tire") &&
                checkStock(cartItems, selectedTire.rear_tire_sku, PACKAGE_QTY, selectedTire.rear_tire_quantity, "Rear Tire");

            if (!canAdd) {
                setLoader(false);
                return;
            }

            // Add Wheels
            await addItem({
                product_id: wheel.front_wheel_sku,
                product_price: wheel.front_wheel_price,
                quantity: PACKAGE_QTY,
                category_type: "wheel",
                image: getSafeImage(selectedTire.front_tire_image),
                vendor_id: wheel.vendor_id,
                total_quantity: wheel.front_wheel_quantity,
                product_name: `${wheel.brand_desc}  ${wheel.style} ${wheel.front_fancy_finish_desc}`,
            });

            await addItem({
                product_id: wheel.rear_wheel_sku,
                product_price: wheel.rear_wheel_price,
                quantity: PACKAGE_QTY,
                category_type: "wheel",
                image: getSafeImage(selectedTire.rear_tire_image || selectedTire.front_tire_image),
                vendor_id: wheel.vendor_id,
                total_quantity: wheel.rear_wheel_quantity,
                product_name: `${wheel.brand_desc} ${wheel.style} ${wheel.rear_fancy_finish_desc}`,
            });

            // Add Tires
            await addItem({
                product_id: selectedTire.front_tire_sku,
                product_price: selectedTire.front_tire_price,
                quantity: PACKAGE_QTY,
                category_type: "tires_cat",
                image: selectedTire.front_tire_image,
                vendor_id: selectedTire.tire_vendor_id,
                total_quantity: selectedTire.front_tire_quantity,
                product_name: `${selectedTire.front_tire_brand} ${selectedTire.front_tire_model} ${selectedTire.front_tire_size} `,
            });

            await addItem({
                product_id: selectedTire.rear_tire_sku,
                product_price: selectedTire.rear_tire_price,
                quantity: PACKAGE_QTY,
                category_type: "tires_cat",
                image: selectedTire.rear_tire_image || selectedTire.front_tire_image,
                vendor_id: selectedTire.tire_vendor_id,
                total_quantity: selectedTire.rear_tire_quantity,
                product_name: `${selectedTire.front_tire_brand} ${selectedTire.rear_tire_model} ${selectedTire.rear_tire_size}`,
            });

            window.dispatchEvent(new Event("cartUpdated"));
            toast.success("Package Added to cart successfully!", {
                position: "top-center",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored",
            });

            setTimeout(() => navigate("/add-cart"), 1000);
        } catch (error) {
            console.error(error);
            toast.error("Failed to add package to cart");
        } finally {
            setLoader(false);
        }
    };

    const goToWheelDetails = () => window.open(`/wheels-product-details/${wheel.front_wheel_sku}`, "_blank");
    const goToTireDetails = (id) => window.open(`/tires-details/${id}`, "_blank");


    const formatWheelSize = (size) => {
        if (!size) return "N/A";

        return size
            .split("x")
            .map(part => Number(part).toString())
            .join("X");
    };

    return (
        <div className="ep-page">
            <div className="ep-builder-layout">
                <main className="ep-main-content">
                    {/* Wheel Section */}
                    <div className="ep-section-card">
                        <div className="ep-section-top">
                            <span className="ep-step">1</span>
                            <span className="ep-section-title">Selected Wheels</span>
                            <button className="ep-edit-wheel-btn" onClick={() => navigate(-1)}>
                                Edit Wheels
                            </button>
                        </div>

                        <div className="ep-wheel-layout">
                            <div className="ep-wheel-image-col">
                                {/* <img
                                    src={selectedImage}
                                    alt={wheel?.brand_desc}
                                    className="ep-wheel-image"
                                /> */}

                                {selectedImage ? (
                                    <img
                                        src={selectedImage}
                                        alt={wheel?.brand_desc || "Wheel"}
                                        className="ep-wheel-image"
                                    />
                                ) : null}

                                <div className="ep-wheel-image placeholder" style={{ display: selectedImage ? "none" : "flex" }}>
                                    No Image Available
                                </div>

                                <div className="ep-wheel-thumbs">
                                    {wheelImages.map((img, index) => (
                                        <img
                                            key={index}                    // Use index as key
                                            src={img}
                                            alt={`Wheel ${index + 1}`}
                                            className={selectedImage === img && index === activeThumbIndex ? "active-thumb" : ""}
                                            onClick={() => {
                                                setSelectedImage(img);
                                                setActiveThumbIndex(index);   // Track which thumbnail was clicked
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div className="ep-wheel-details">
                                <div className="ep-wheel-brand">{wheel?.brand_desc}</div>
                                {/* <h2 className="ep-wheel-title">Staggered Wheel Set</h2> */}

                                {/* FRONT WHEEL */}
                                <div className="mt-2">
                                    <div className="fw-bold text-danger mb-2" style={{ fontSize: "14px" }}>
                                        FRONT WHEEL
                                    </div>
                                    <div className="ep-wheel-spec-list">
                                        <div className="ep-spec-row">
                                            <span className="ep-spec-label">SIZE</span>
                                            <span className="ep-spec-value">
                                                {formatWheelSize(wheel?.front_wheel_size)}
                                            </span>
                                        </div>
                                        <div className="ep-spec-row">
                                            <span className="ep-spec-label">SKU</span>
                                            <span className="ep-spec-value">{wheel?.front_wheel_sku || "N/A"}</span>
                                        </div>
                                        <div className="ep-spec-row">
                                            <span className="ep-spec-label">STYLE</span>
                                            <span className="ep-spec-value">{wheel?.style || "N/A"}</span>
                                        </div>
                                        <div className="ep-spec-row">
                                            <span className="ep-spec-label">FINISH</span>
                                            <span className="ep-spec-value">{wheel?.front_fancy_finish_desc || "N/A"}</span>
                                        </div>
                                    </div>
                                    {/* <div className="ep-wheel-price">
                                        <strong>${wheel?.front_wheel_price}</strong>
                                    </div> */}

                                    <a
                                        href="#"
                                        className="pkg-cta-btn danger mt-2"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.open(`/wheels-product-details/${wheel.front_wheel_sku}`, "_blank");
                                        }}
                                    >
                                        View Details
                                    </a>
                                </div>

                                {/* REAR WHEEL */}
                                <div className="mt-2">
                                    <div className="fw-bold text-danger mb-2" style={{ fontSize: "14px" }}>
                                        REAR WHEEL
                                    </div>
                                    <div className="ep-wheel-spec-list">
                                        <div className="ep-spec-row">
                                            <span className="ep-spec-label">SIZE</span>
                                            <span className="ep-spec-value">
                                                {formatWheelSize(wheel?.rear_wheel_size)}
                                            </span>
                                        </div>
                                        <div className="ep-spec-row">
                                            <span className="ep-spec-label">SKU</span>
                                            <span className="ep-spec-value">{wheel?.rear_wheel_sku || "N/A"}</span>
                                        </div>
                                        <div className="ep-spec-row">
                                            <span className="ep-spec-label">FINISH</span>
                                            <span className="ep-spec-value">{wheel?.rear_fancy_finish_desc || "N/A"}</span>
                                        </div>
                                    </div>
                                    {/* <div className="ep-wheel-price">
                                        <strong>${wheel?.rear_wheel_price}</strong>
                                    </div> */}

                                    <a
                                        href="#"
                                        className="pkg-cta-btn danger mt-3"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.open(`/wheels-product-details/${wheel.rear_wheel_sku}`, "_blank");
                                        }}
                                    >
                                        View Details
                                    </a>
                                </div>

                                <div className="ep-wheel-price mt-4">
                                    <strong>${wheelTotal.toFixed(2)}</strong>
                                </div>


                            </div>
                        </div>
                    </div>

                    {/* Tire Section */}
                    {/* Tire Section - Responsive */}
                    <div className="ep-section-card">
                        <div className="ep-section-top">
                            <span className="ep-step">2</span>
                            <span className="ep-section-title">Select Your Tires</span>
                            <span className="ep-selected">{tireOptions.length} Options Available</span>
                        </div>

                        <div className="ep-tire-list">
                            {tireOptions.map((tire) => {
                                const tireKey = getTireSetKey(tire);
                                const isActive = selectedTire && getTireSetKey(selectedTire) === tireKey;

                                const frontPrice = Number(tire.front_tire_price || 0);
                                const rearPrice = Number(tire.rear_tire_price || 0);
                                const setPrice = (frontPrice * 2) + (rearPrice * 2);

                                return (
                                    <div
                                        key={tireKey}
                                        className={`ep-tire-card ${isActive ? "active" : ""}`}
                                        onClick={() => setSelectedTire(tire)}
                                    >
                                        <div className="ep-radio"><div /></div>

                                        {/* Image - Centered on Mobile, Left on Desktop */}
                                        <div className="tire-image-container text-center mb-3 mb-md-0">
                                            {tire.front_tire_image ? (
                                                <img
                                                    src={tire.front_tire_image}
                                                    alt={tire.front_tire_brand || "Tire"}
                                                    className="ep-tire-image"
                                                />
                                            ) : (
                                                <div className="ep-tire-image placeholder">
                                                    No Image Available
                                                </div>
                                            )}
                                        </div>

                                        {/* Details */}
                                        <div className="ep-tire-content flex-grow-1">
                                            <div className="ep-tire-brand text-center text-md-start">
                                                {tire.front_tire_brand} {tire.front_tire_model || ""}
                                            </div>

                                            <div className="row mt-2">
                                                {/* Front */}
                                                <div className="col-6">
                                                    <strong>Front:</strong> {tire.front_tire_size}<br />
                                                    {/* {tire.front_tire_size}<br /> */}
                                                    <span className="pkg-tire-sku">SKU: {tire.front_tire_sku}</span>
                                                    <div className="fw-bold mt-1" style={{ fontSize: "14px" }}> ${frontPrice.toFixed(2)} <span>/each</span></div>
                                                    <div className="ep-stock">
                                                        <i className="fa fa-industry text-success" aria-hidden="true"></i>{" "}
                                                        {tire.front_tire_quantity}
                                                    </div>

                                                    <a
                                                        href="#"
                                                        className="pkg-cta-stn-btn danger d-block mt-2"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            goToTireDetails(tire.front_tire_id);
                                                        }}
                                                    >
                                                        View Details
                                                    </a>
                                                </div>

                                                {/* Rear */}
                                                <div className="col-6">
                                                    <strong>Rear:</strong> {tire.rear_tire_size}<br />
                                                    {/* {tire.rear_tire_size}<br /> */}
                                                    <span className="pkg-tire-sku">SKU: {tire.rear_tire_sku}</span>
                                                    <div className="fw-bold mt-1" style={{ fontSize: "14px" }}>${rearPrice.toFixed(2)} <span>/each</span></div>
                                                    <div className="ep-stock">
                                                        <i className="fa fa-industry text-success" aria-hidden="true"></i>{" "}
                                                        {tire.rear_tire_quantity}
                                                    </div>

                                                    <a
                                                        href="#"
                                                        className="pkg-cta-stn-btn danger d-block mt-2"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            goToTireDetails(tire.rear_tire_id || tire.front_tire_id);
                                                        }}
                                                    >
                                                        View Details
                                                    </a>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Set Price */}
                                        <div className="ep-tire-price text-end mt-3 mt-md-0">
                                            <div className="fw-bold text-danger fs-5">
                                                ${setPrice.toFixed(2)}
                                            </div>
                                            <small className="text-muted">Full Set (4)</small>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </main>

                {/* Summary Sidebar */}
                {/* Summary Sidebar */}
                <aside className="ep-summary-sidebar">
                    <div className="ep-summary-card">
                        <div className="ep-summary-header">PACKAGE SUMMARY</div>

                        {/* Wheels Summary - Split Front & Rear */}
                        <div className="ep-summary-product">
                            {/* <img src={wheel?.image_url1} alt={wheel?.brand_desc} className="ep-summary-product-image" /> */}
                            {wheel?.image_url1 ? (
                                <img
                                    src={wheel.image_url1}
                                    alt={wheel?.brand_desc || "Wheel"}
                                    className="ep-summary-product-image"
                                    onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                        e.currentTarget.nextSibling.style.display = "flex";
                                    }}
                                />
                            ) : (
                                <div className="ep-summary-product-image placeholder">
                                    No Image Available
                                </div>
                            )}
                            <div className="ep-summary-product-info">
                                <div className="ep-summary-product-label">Wheels</div>
                                <div className="ep-summary-product-name">{wheel?.brand_desc}</div>

                                <div className="ep-summary-product-spec">
                                    <div><strong>Front (2):</strong> {formatWheelSize(wheel?.front_wheel_size)}</div>
                                    <div><strong>Rear (2):</strong> {formatWheelSize(wheel?.rear_wheel_size)} </div>
                                </div>
                            </div>
                            <div className="ep-summary-product-price">
                                ${wheelTotal.toFixed(2)}
                            </div>
                        </div>

                        {/* Tires Summary - Split Front & Rear */}
                        {selectedTire && (
                            <div className="ep-summary-product">
                                {selectedTire?.front_tire_image ? (
                                    <img
                                        src={selectedTire.front_tire_image}
                                        alt={selectedTire.front_tire_brand}
                                        className="ep-summary-product-image"
                                    />
                                ) : (
                                    <div className="ep-summary-product-image placeholder">
                                        No Image Available
                                    </div>
                                )}
                                <div className="ep-summary-product-info">
                                    <div className="ep-summary-product-label">Tires</div>
                                    <div className="ep-summary-product-name">{selectedTire.front_tire_brand}</div>

                                    <div className="ep-summary-product-spec">
                                        <div><strong>Front (2):</strong> {selectedTire.front_tire_size}</div>
                                        <div><strong>Rear (2):</strong> {selectedTire.rear_tire_size}</div>
                                    </div>
                                </div>
                                <div className="ep-summary-product-price">
                                    ${((Number(selectedTire.front_tire_price || 0) * 2) +
                                        (Number(selectedTire.rear_tire_price || 0) * 2)).toFixed(2)}
                                </div>
                            </div>
                        )}

                        <div className="ep-summary-total">
                            <label>PACKAGE TOTAL</label>
                            <div>${packageTotal}</div>
                        </div>

                        {!loader ? (
                            <button
                                className="ep-cart-btn"
                                onClick={addPackageToCart}
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle cx="9" cy="21" r="1" />
                                    <circle cx="20" cy="21" r="1" />
                                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                </svg>
                                ADD PACKAGE TO CART
                            </button>
                        ) : (
                            <button
                                className="ep-cart-btn"
                                disabled
                            >
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                />
                                ADDING TO CART...
                            </button>
                        )}
                    </div>
                </aside>
            </div>
        </div>
    );
}